import { useCallback, useContext, useEffect, useState } from 'react';

import { getStaffRequest } from '@vyce/core/src/api/companies';
import {
  createScheduledNotificationRequest,
  deleteScheduledNotificationsRequest,
  getScheduledNotificationsRequest,
  restoreScheduledNotificationsRequest,
  updateScheduledNotificationRequest,
} from '@vyce/core/src/api/notifications';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { ScheduledNotification } from '@vyce/core/src/types';

import { DAILY_SNAPSHOT_NAME, DEFAULT_ROLES } from '../constants';
import {
  SnapshotFormValue,
  formSnapshotToScheduledNotification,
  scheduledNotificationToFormSnapshot,
} from '../types';

interface UseScheduledNotificationsProps {
  companyId?: string;
  locationIds?: string[];
}

interface SaveDailyParams {
  dailySnapshots: SnapshotFormValue[];
  locationsCount: number;
}

interface SavedNotification {
  id: string;
  notification: ScheduledNotification;
  is_active?: boolean;
}

export const useScheduledNotifications = ({
  companyId,
  locationIds = [],
}: UseScheduledNotificationsProps) => {
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const [notifications, setNotifications] = useState<SavedNotification[]>([]);
  const [recipientIdToName, setRecipientIdToName] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [togglingNotificationId, setTogglingNotificationId] = useState<string | null>(null);

  const fetchNotifications = useCallback(
    async (silent = false) => {
      if (!companyId) return;

      if (!silent) setLoading(true);
      try {
        const { data } = await getScheduledNotificationsRequest({ companyId });
        const items = Array.isArray(data) ? data : data?.items ?? data?.data ?? [];
        const dailyOnly = items.filter(
          (item: any) =>
            item.name === DAILY_SNAPSHOT_NAME || item.name?.startsWith(`${DAILY_SNAPSHOT_NAME} `)
        );

        const recipientIds = Array.from(
          new Set(dailyOnly.flatMap((item: any) => item.config?.recipient_user_ids ?? []))
        ).filter(Boolean);

        let idToName: Record<string, string> = {};
        if (recipientIds.length > 0) {
          try {
            const { data: staffData } = await getStaffRequest(companyId, {
              limit: 1000,
              offset: 0,
              substring: '',
              roles: DEFAULT_ROLES,
            });
            const staffItems = staffData?.items ?? [];
            staffItems.forEach((item: { user_id: string; first_name: string; last_name: string }) => {
              idToName[item.user_id] = `${item.first_name} ${item.last_name}`;
            });
          } catch {}
          setRecipientIdToName(idToName);
        }

        setNotifications(
          dailyOnly.map((item: any) => ({
            id: item.id ?? item.uuid,
            notification: {
              channel: item.channel ?? 'email',
              name: item.name,
              config: item.config ?? {},
            },
            is_active: item.is_active ?? true,
          }))
        );
      } catch (e) {
        handleServerError(e);
        setNotifications([]);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [companyId, handleServerError]
  );

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const getDailyFormDefaults = useCallback((): SnapshotFormValue[] => {
    return notifications.map(n => ({
      ...scheduledNotificationToFormSnapshot(n.notification, locationIds, recipientIdToName),
      notificationId: n.id,
      isActive: n.is_active ?? true,
    }));
  }, [notifications, locationIds, recipientIdToName]);

  const deleteDailySnapshot = useCallback(
    async (scheduledNotificationId: string) => {
      if (!companyId) return;
      try {
        await deleteScheduledNotificationsRequest({
          companyId,
          scheduledNotificationId,
        });
        await fetchNotifications();
        showNotification({
          message: 'Notification deleted successfully',
          options: { variant: 'success' },
        });
      } catch (e) {
        handleServerError(e);
      }
    },
    [companyId, fetchNotifications, handleServerError, showNotification]
  );

  const toggleDailySnapshot = useCallback(
    (scheduledNotificationId: string, activate: boolean) => {
      if (!companyId) return;
      setTogglingNotificationId(scheduledNotificationId);
      const request = activate
        ? restoreScheduledNotificationsRequest({ companyId, scheduledNotificationId })
        : deleteScheduledNotificationsRequest({ companyId, scheduledNotificationId });
      request
        .then(() => fetchNotifications(true))
        .then(() =>
          showNotification({
            message: activate ? 'Notification activated' : 'Notification deactivated',
            options: { variant: 'success' },
          })
        )
        .catch(handleServerError)
        .finally(() => setTogglingNotificationId(null));
    },
    [companyId, handleServerError, showNotification]
  );

  const saveDailySnapshots = useCallback(
    async ({ dailySnapshots, locationsCount }: SaveDailyParams) => {
      if (!companyId) return;

      setSaving(true);
      try {
        const existingDaily = notifications;

        const toCreate: ScheduledNotification[] = [];
        const toUpdate: { id: string; notification: ScheduledNotification }[] = [];

        const nameNumbers = dailySnapshots
          .map(s => s.name?.match(new RegExp(`${DAILY_SNAPSHOT_NAME} (\\d+)`))?.[1])
          .filter(Boolean)
          .map(Number);
        const maxNum = nameNumbers.length > 0 ? Math.max(...nameNumbers) : 0;
        let nextIncrement = maxNum + 1;

        dailySnapshots.forEach((snapshot, index) => {
          const notificationName = snapshot.notificationId
            ? snapshot.name ?? `${DAILY_SNAPSHOT_NAME} ${index + 1}`
            : `${DAILY_SNAPSHOT_NAME} ${nextIncrement++}`;
          const notification = formSnapshotToScheduledNotification(
            snapshot,
            notificationName,
            locationsCount
          );
          const existing = existingDaily[index];
          if (existing) {
            toUpdate.push({ id: existing.id, notification });
          } else {
            toCreate.push(notification);
          }
        });

        await Promise.all([
          ...toCreate.map(n => createScheduledNotificationRequest({ companyId, scheduledNotification: n })),
          ...toUpdate.map(({ id, notification }) =>
            updateScheduledNotificationRequest({
              companyId,
              scheduledNotificationId: id,
              scheduledNotification: notification,
            })
          ),
        ]);

        await fetchNotifications();
        showNotification({
          message: 'Notifications saved successfully',
          options: { variant: 'success' },
        });
      } catch (e) {
        handleServerError(e);
      } finally {
        setSaving(false);
      }
    },
    [companyId, notifications, fetchNotifications, handleServerError, showNotification]
  );

  return {
    dailyNotifications: notifications,
    loading,
    saving,
    togglingNotificationId,
    fetchNotifications,
    saveDailySnapshots,
    deleteDailySnapshot,
    toggleDailySnapshot,
    getDailyFormDefaults,
  };
};
